import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Menu, MenuItem, Snackbar, Paper, Portal } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ProgressBar from "../../UI/ProgressBar";
import { CreateEventWizardModal } from "./CreateEventWizardModal";
import { AddToCreatedEventModal } from "./AddToCreatedEventModal";
import { MarkPromotionCompletedModal } from "./MarkPromotionCompletedModal";
import { PromotionsEligibilityFilters } from "./PromotionsEligibilityFilters";
import { PromotionsBookTabs } from "./PromotionsBookTabs";
import { PromotionsEligibilityTable } from "./PromotionsEligibilityTable";
import { usePromotionsEligibilityList } from "./usePromotionsEligibilityList";
import {
  getPromotionSelectionModel,
  nextIdsAfterHeaderToggle,
  nextIdsAfterRowToggle,
} from "./promotionSelection";
import { PROMOTIONS_LIST_MODES } from "./promotionListModes";
import { useCompleteContactPendingPromotionsMutation } from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { useAlert } from "../../../hooks/useAlert";
import "./Promotions.css";
import { useNavigate } from "react-router-dom";

export const Promotions = () => {
  const navigate = useNavigate();
  const { displayAlert } = useAlert();
  const list = usePromotionsEligibilityList(PROMOTIONS_LIST_MODES.CREATE);

  const [selectedIds, setSelectedIds] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSeedIds, setWizardSeedIds] = useState([]);
  const [addToEventOpen, setAddToEventOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [markAsMenuAnchor, setMarkAsMenuAnchor] = useState(null);
  const [completePending, { isLoading: isCompleting }] =
    useCompleteContactPendingPromotionsMutation();

  useEffect(() => {
    setSelectedIds([]);
  }, [
    list.selectedBookId,
    list.searchQuery,
    list.companyIds,
    list.dateField,
    list.dateStart,
    list.dateEnd,
  ]);

  const selectionModel = useMemo(
    () => getPromotionSelectionModel(list.promotions, selectedIds),
    [list.promotions, selectedIds]
  );
  const selectedIdSet = useMemo(
    () => new Set(selectionModel.visibleSelectedIds),
    [selectionModel.visibleSelectedIds]
  );
  const selectedCount = selectionModel.visibleSelectedIds.length;
  const selectionToastHidden = wizardOpen || addToEventOpen || completeOpen;

  const tableWrapRef = useRef(null);
  const [toastBox, setToastBox] = useState(null);

  const updateToastBox = useCallback(() => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const table = wrap.querySelector(".promotions-table") || wrap;
    const rect = table.getBoundingClientRect();
    setToastBox({
      center: rect.left + rect.width / 2,
      minWidth: rect.width * 0.6,
      maxWidth: rect.width * 0.85,
    });
  }, []);

  useLayoutEffect(() => {
    updateToastBox();
    const el = tableWrapRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(updateToastBox);
    observer.observe(el);
    window.addEventListener("resize", updateToastBox);
    window.addEventListener("scroll", updateToastBox, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateToastBox);
      window.removeEventListener("scroll", updateToastBox, true);
    };
  }, [updateToastBox, selectedCount]);

  useEffect(() => {
    if (selectedCount) return;
    setAddToEventOpen(false);
    setMarkAsMenuAnchor(null);
  }, [selectedCount]);

  const openWizard = (seedIds = []) => {
    setWizardSeedIds(seedIds);
    setWizardOpen(true);
  };

  const handleInviteSuccess = (invite) => {
    setSelectedIds([]);
    setWizardSeedIds([]);
    setWizardOpen(false);
    if (invite?._id) {
      navigate(`/super-admin/promotions/history/${invite._id}`);
    }
  };

  const confirmMarkCompleted = async () => {
    const promotionIds = selectionModel.visibleSelectedIds;
    if (!promotionIds.length) return;
    try {
      const result = await completePending({ promotionIds }).unwrap();
      displayAlert({
        open: true,
        message:
          result?.message ||
          "Employee marked as Promotion Completed",
        type: "success",
        timeout: "2500",
      });
      setSelectedIds([]);
      setCompleteOpen(false);
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message ||
          err?.message ||
          "Unable to mark Promotion Completed",
        type: "error",
        timeout: "3000",
      });
    }
  };

  if (list.booksLoading) {
    return <ProgressBar />;
  }

  return (
    <div className="promotions-page container">
      <PromotionsEligibilityFilters
        mode={PROMOTIONS_LIST_MODES.CREATE}
        searchInput={list.searchInput}
        onSearchChange={list.onSearchChange}
        dateField={list.dateField}
        dateStart={list.dateStart}
        dateEnd={list.dateEnd}
        onDateApply={list.onDateApply}
        onDateReset={list.onDateReset}
        companies={list.companies}
        companyIds={list.companyIds}
        onCompanyIdsApply={list.setCompanyIds}
        toolbarEnd={
          <Button
            variant="contained"
            className="promotions-page__btn-create-invite"
            disableElevation
            onClick={() => openWizard([])}
          >
            Create Event
          </Button>
        }
      />

      <PromotionsBookTabs
        books={list.books}
        selectedBookId={list.selectedBookId}
        onChange={list.setSelectedBookId}
      />

      <div className="promotions-page__table-wrap" ref={tableWrapRef}>
        {(list.isLoading || list.isFetching) && <ProgressBar />}
        <PromotionsEligibilityTable
          mode={PROMOTIONS_LIST_MODES.CREATE}
          promotions={list.promotions}
          selectionModel={selectionModel}
          selectedIdSet={selectedIdSet}
          onSelectAllMatching={() =>
            setSelectedIds(nextIdsAfterHeaderToggle(selectionModel))
          }
          onToggleOne={(row, checked) =>
            setSelectedIds(nextIdsAfterRowToggle(row, checked, selectionModel))
          }
          emptyMessage="No contact-pending employees for this book"
        />
      </div>

      <Portal>
        <Snackbar
          open={selectedCount > 0 && !selectionToastHidden}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          className="promotions-selection-toast"
          sx={{
            "--promo-toast-center": toastBox
              ? `${toastBox.center}px`
              : undefined,
            "--promo-toast-min-width": toastBox
              ? `${toastBox.minWidth}px`
              : undefined,
            "--promo-toast-max-width": toastBox
              ? `${toastBox.maxWidth}px`
              : undefined,
          }}
        >
          <Paper
            elevation={0}
            className="promotions-selection-toast__content"
            role="status"
          >
            <span className="promotions-selection-toast__count">
              ({selectedCount}) Selected
            </span>
            <div className="promotions-selection-toast__actions">
              <Button
                className="promotions-page__btn-reset"
                variant="contained"
                disableElevation
                onClick={() => {
                  list.resetFilters();
                  setSelectedIds([]);
                }}
              >
                Reset
              </Button>
              <Button
                className="promotions-page__btn-create-invite"
                variant="contained"
                disableElevation
                onClick={() =>
                  openWizard(selectionModel.visibleSelectedIds)
                }
              >
                Create Event
              </Button>
              <Button
                className="promotions-page__btn-add-to-event"
                variant="contained"
                disableElevation
                onClick={() => setAddToEventOpen(true)}
              >
                Add to Event
              </Button>
              <Button
                className="promotions-page__btn-mark"
                variant="contained"
                disableElevation
                onClick={(e) =>
                  setMarkAsMenuAnchor((current) =>
                    current ? null : e.currentTarget
                  )
                }
                endIcon={<KeyboardArrowDownIcon />}
                aria-label="Mark As"
                aria-haspopup="menu"
                aria-expanded={Boolean(markAsMenuAnchor)}
              >
                Mark As
              </Button>
            </div>
          </Paper>
        </Snackbar>
      </Portal>

      <Menu
        anchorEl={markAsMenuAnchor}
        open={Boolean(markAsMenuAnchor)}
        onClose={() => setMarkAsMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ zIndex: 1500 }}
        PaperProps={{ className: "promo-history-actions-menu" }}
      >
        <MenuItem
          onClick={() => {
            setMarkAsMenuAnchor(null);
            setCompleteOpen(true);
          }}
        >
          Promotion Completed
        </MenuItem>
      </Menu>

      <CreateEventWizardModal
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setWizardSeedIds([]);
        }}
        books={list.books}
        defaultBookId={list.selectedBookId || ""}
        initialPromotionIds={wizardSeedIds}
        onSuccess={handleInviteSuccess}
      />

      <AddToCreatedEventModal
        open={addToEventOpen}
        onClose={() => setAddToEventOpen(false)}
        bookId={list.selectedBookId}
        bookName={list.selectedBookName}
        promotionIds={selectionModel.visibleSelectedIds}
        onSuccess={() => {
          setSelectedIds([]);
          setAddToEventOpen(false);
        }}
      />

      <MarkPromotionCompletedModal
        open={completeOpen}
        count={selectedCount}
        confirming={isCompleting}
        onClose={() => {
          if (isCompleting) return;
          setCompleteOpen(false);
        }}
        onConfirm={confirmMarkCompleted}
      />
    </div>
  );
};
